/**
 * Calendar-driven active-school-year resolver (v3.29.0).
 *
 * Loads the Academic Calendar (default `quarters` collection, overlaid by the
 * user's district calendar in `districtCalendars/{districtId}/quarters` — the
 * district-specific dates win, matching getCurrentQuarter()) and derives which
 * school year is active TODAY via deriveSchoolYearFromCalendar().
 *
 * When the calendar says no year is active, teacher pages show a
 * SchoolYearNotice and load no data ("the school academic calendar has not
 * started yet" — Kemal, 2026-07-13). Only when NO calendar data exists at all
 * do we fall back to the July 1 rule so a fresh install isn't bricked.
 *
 * Results are cached per districtId for the session (calendars change rarely;
 * admins can refresh the page after editing them).
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { deriveSchoolYearFromCalendar, schoolYearStartYear } from './schoolYear';

const cache = new Map(); // key: districtId || 'default' → Promise<result>
const calendarCache = new Map(); // key: districtId || 'default' → Promise<quartersData>

// Quarter docs are named "YYYYQN" (e.g. "2025Q4"). Anything else in the
// collection — `_schema_metadata` and friends — is not calendar data.
// deriveSchoolYearFromCalendar() already ignores non-numeric year keys, so
// this only keeps the parsed shape clean for callers that read it directly.
const QUARTER_DOC_ID_RE = /^\d{4}Q[1-4]$/;

async function loadQuartersData(districtId) {
  const quartersData = {};
  const ingest = (snap) => {
    snap.docs.forEach(d => {
      if (!QUARTER_DOC_ID_RE.test(d.id)) return;
      const [year, quarter] = d.id.split('Q');
      if (!quartersData[year]) quartersData[year] = {};
      quartersData[year][quarter] = d.data(); // district-specific dates win
    });
  };
  ingest(await getDocs(collection(db, 'quarters')));
  if (districtId) {
    try {
      ingest(await getDocs(collection(db, `districtCalendars/${districtId}/quarters`)));
    } catch {
      // No district calendar — default applies.
    }
  }
  return quartersData;
}

/**
 * The Academic Calendar itself (`{year: {quarter: {start, end}}}`), default
 * collection overlaid by the district calendar — cached per districtId for the
 * session, shared with resolveActiveSchoolYear().
 *
 * Screens need BOTH the derived active year and the raw calendar (to label the
 * current quarter). Before v3.37.0 each screen called resolveActiveSchoolYear()
 * and then re-fetched `quarters` + `districtCalendars/{id}/quarters` itself —
 * two extra SEQUENTIAL round trips and a duplicate read of the same handful of
 * docs, on seven screens (2026-07 perf audit, step 5). Calendars change a few
 * times a year and admins already refresh after editing them, so one
 * session-scoped fetch serves every caller.
 *
 * A failed load is evicted rather than cached, so a Retry actually re-fetches.
 *
 * @param {string|null} districtId  user?.profile?.districtId
 * @returns {Promise<Object>} quartersData
 */
export function getCalendarQuarters(districtId = null) {
  const key = districtId || 'default';
  if (!calendarCache.has(key)) {
    const pending = loadQuartersData(districtId).catch((err) => {
      calendarCache.delete(key); // don't cache a failure — Retry must re-fetch
      throw err;
    });
    calendarCache.set(key, pending);
  }
  return calendarCache.get(key);
}

/**
 * Resolve the active school year for a user's district.
 *
 * @param {string|null} districtId  user?.profile?.districtId
 * @returns {Promise<
 *   { status: 'active', startYear: number, source: 'calendar' | 'fallback' } |
 *   { status: 'not_started', nextStartYear: number, nextStartDate: Date } |
 *   { status: 'not_configured' }
 * >}
 */
export function resolveActiveSchoolYear(districtId = null) {
  const key = districtId || 'default';
  if (!cache.has(key)) {
    cache.set(key, (async () => {
      let quartersData;
      try {
        quartersData = await getCalendarQuarters(districtId);
      } catch (err) {
        console.error('resolveActiveSchoolYear: calendar load failed, using July-1 fallback', err);
        return { status: 'active', startYear: schoolYearStartYear(), source: 'fallback' };
      }
      if (Object.keys(quartersData).length === 0) {
        // No calendar configured anywhere — keep the app usable.
        return { status: 'active', startYear: schoolYearStartYear(), source: 'fallback' };
      }
      const derived = deriveSchoolYearFromCalendar(quartersData, new Date());
      return derived.status === 'active' ? { ...derived, source: 'calendar' } : derived;
    })());
  }
  return cache.get(key);
}

/** Test/logout hook: clear the per-session calendar cache. */
export function clearActiveSchoolYearCache() {
  cache.clear();
  calendarCache.clear();
}
